import { useCallback, useEffect, useRef, useState } from "react";
import type { DraftSaveRequest } from "@sga/shared";

/**
 * The builder's whole-schema state, with undo/redo and autosave.
 *
 * "Keep the whole schema in a history stack; the whole-schema PUT makes this trivial"
 * (docs/04-BUILD-PLAN.md Phase 4) — so undo is just an index into an array of complete schemas,
 * not a log of reversible operations. At ~50 entries of a 60-field form this is a few hundred KB,
 * which is a fair trade for making undo impossible to get subtly wrong.
 */

const HISTORY_LIMIT = 50;
const AUTOSAVE_INTERVAL_MS = 15_000;

export type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";

export interface BuilderState {
  schema: DraftSaveRequest;
  /** Replaces the schema and pushes a history entry. */
  update: (next: DraftSaveRequest | ((current: DraftSaveRequest) => DraftSaveRequest)) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  saveState: SaveState;
  lastSavedAt: Date | null;
  /** Force a save now — called on blur and before publishing. */
  saveNow: () => Promise<void>;
  /** Discard the locally recovered draft and reload from the server copy. */
  resetTo: (schema: DraftSaveRequest) => void;
}

const localDraftKey = (formId: string): string => `sga:builder-draft:${formId}`;

/**
 * Whether the builder is allowed to persist its schema — to the server or to the local mirror.
 *
 * Hydration is the load-bearing half. An unhydrated builder holds the empty placeholder schema,
 * so persisting it replaces a real draft with nothing. This is deliberately a named, exported
 * predicate rather than an inline `&&`: it is the guard that stands between a failed draft fetch
 * and silent, irreversible loss of an author's work.
 */
export const canPersist = (hydrated: boolean, saveState: SaveState): boolean =>
  hydrated && saveState !== "saving";

export const useBuilderState = (
  formId: string,
  initialSchema: DraftSaveRequest,
  save: (schema: DraftSaveRequest) => Promise<void>,
): BuilderState => {
  // History and cursor are one piece of state, not two: they must always move together, and
  // splitting them invites an update that advances one without the other.
  const [{ history, cursor }, setTimeline] = useState<{
    history: DraftSaveRequest[];
    cursor: number;
  }>({ history: [initialSchema], cursor: 0 });
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // The builder must never persist a schema it never loaded. `resetTo` runs once the server draft
  // — or a recovered local mirror — has actually arrived; until then `initialSchema` is the empty
  // placeholder, and saving it would overwrite a real draft with nothing. A dashboard left open
  // across an API restart did exactly that and destroyed a 26-field form: the draft fetch failed,
  // the builder rendered empty but editable, and the 15s autosave wrote the emptiness back.
  const hydratedRef = useRef(false);

  const schema = history[cursor] ?? initialSchema;

  // Refs so the autosave interval always sees current values without being re-created each render.
  const schemaRef = useRef(schema);
  const saveStateRef = useRef(saveState);
  schemaRef.current = schema;
  saveStateRef.current = saveState;

  const update = useCallback(
    (next: DraftSaveRequest | ((current: DraftSaveRequest) => DraftSaveRequest)) => {
      setTimeline((timeline) => {
        const current = timeline.history[timeline.cursor];
        if (!current) return timeline;

        const resolved = typeof next === "function" ? next(current) : next;

        // Editing after an undo discards the redo branch — standard editor behaviour.
        const branch = [...timeline.history.slice(0, timeline.cursor + 1), resolved];
        const trimmed =
          branch.length > HISTORY_LIMIT ? branch.slice(branch.length - HISTORY_LIMIT) : branch;

        return { history: trimmed, cursor: trimmed.length - 1 };
      });
      setSaveState("dirty");
    },
    [],
  );

  const undo = useCallback(() => {
    setTimeline((timeline) => ({ ...timeline, cursor: Math.max(0, timeline.cursor - 1) }));
    setSaveState("dirty");
  }, []);

  const redo = useCallback(() => {
    setTimeline((timeline) => ({
      ...timeline,
      cursor: Math.min(timeline.history.length - 1, timeline.cursor + 1),
    }));
    setSaveState("dirty");
  }, []);

  const resetTo = useCallback(
    (next: DraftSaveRequest) => {
      setTimeline({ history: [next], cursor: 0 });
      setSaveState("idle");
      hydratedRef.current = true;
      window.localStorage.removeItem(localDraftKey(formId));
    },
    [formId],
  );

  const saveNow = useCallback(async () => {
    if (!canPersist(hydratedRef.current, saveStateRef.current)) return;
    setSaveState("saving");
    try {
      await save(schemaRef.current);
      setSaveState("saved");
      setLastSavedAt(new Date());
      // The local mirror exists only to survive a crash between autosaves; once the server has
      // the schema it is redundant, and keeping it would resurrect stale edits on next load.
      window.localStorage.removeItem(localDraftKey(formId));
    } catch {
      setSaveState("error");
    }
  }, [save, formId]);

  // Autosave every 15 seconds while dirty (Phase 4 prompt).
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (saveStateRef.current === "dirty") void saveNow();
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [saveNow]);

  /**
   * Mirror every change to localStorage immediately. Autosave is every 15s; a browser crash or a
   * closed tab in between would otherwise lose the work. "Reloading mid-edit recovers the
   * autosaved draft" is an acceptance criterion, and 15s of silent data loss would not meet it.
   */
  useEffect(() => {
    // Same hydration guard as `saveNow`: mirroring an unhydrated schema would let the next load
    // "recover" the emptiness and then autosave it, turning a transient fetch failure into loss.
    if (saveState !== "dirty" || !canPersist(hydratedRef.current, saveState)) return;
    try {
      window.localStorage.setItem(
        localDraftKey(formId),
        JSON.stringify({ savedAt: Date.now(), schema }),
      );
    } catch {
      // Private-browsing quota errors are not worth interrupting an edit over.
    }
  }, [schema, saveState, formId]);

  // Save on tab hide as well as on blur — a closed laptop lid should not lose the last 15 seconds.
  useEffect(() => {
    const onHide = (): void => {
      if (saveStateRef.current === "dirty") void saveNow();
    };
    window.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [saveNow]);

  return {
    schema,
    update,
    undo,
    redo,
    canUndo: cursor > 0,
    canRedo: cursor < history.length - 1,
    saveState,
    lastSavedAt,
    saveNow,
    resetTo,
  };
};

/** Reads a locally-mirrored draft, if one survived a crash. */
export const readLocalDraft = (
  formId: string,
): { savedAt: number; schema: DraftSaveRequest } | null => {
  try {
    const raw = window.localStorage.getItem(localDraftKey(formId));
    return raw ? (JSON.parse(raw) as { savedAt: number; schema: DraftSaveRequest }) : null;
  } catch {
    return null;
  }
};

export const clearLocalDraft = (formId: string): void => {
  window.localStorage.removeItem(localDraftKey(formId));
};
